import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../config/AuthContext";
import routeToPermission from "../../config/routeToPermission";

// Sub-breadcrumb definitions
const subCrumbs = {
  Courses: [
    {
      key: "TpPortalMgtCourseCatalog",
      label: "Course Catalog",
      to: "/TpPortalMgtCourseCatalog",
    },
    {
      key: "TpPortalMgtCourseShell",
      label: "Course Shell",
      to: "/TpPortalMgtCourseShell",
    },
    {
      key: "TpCourseShellStatus",
      label: "Credit Capture Status",
      to: "/TpCourseShellStatus",
    },
    { key: "TpShellStatus", label: "Shell Status", to: "/TpShellStatus" },
  ],
  Credit: [
    { key: "TpCreditCapture", label: "Credit Capture", to: "/TpCreditCapture" },
    {
      key: "TpCreditCaptureStatus",
      label: "Credit Capture Status",
      to: "/TpCreditCaptureStatus",
    },
    { key: "TpCreditTypes", label: "Credit Types", to: "/TpCreditTypes" },
  ],
  Elearn: [
    {
      key: "TpElearnShellTerm",
      label: "Elearn Shell Term",
      to: "/TpElearnShellTerm",
    },
    {
      key: "TpElearnShellType",
      label: "Elearn Shell Type",
      to: "/TpElearnShellType",
    },
  ],
  FPS: [
    { key: "TpFps", label: "FPS", to: "/TpFps" },
    { key: "TpFpsCourses", label: "FPS Courses", to: "/TpFpsCourses" },
  ],
  Partners: [
    { key: "TpPartners", label: "Partners", to: "/TpPartners" },
    { key: "TpSites", label: "Sites", to: "/TpSites" },
  ],
  PTP: [
    { key: "TpPTPForms", label: "Forms", to: "/TpPTPForms" },
    { key: "PTPStatusNote", label: "Status Note", to: "/PTPStatusNote" },
  ],
  Students: [
    { key: "TpStudents", label: "Students", to: "/TpStudents" },
    {
      key: "TpStudentArchive",
      label: "Archived Students",
      to: "/TpStudentArchive",
    },
  ],
  "Status Codes": [
    {
      key: "TpResidenceStatusCodes",
      label: "Residence",
      to: "/TpResidenceStatusCodes",
    },
    { key: "TpSsnStatusCode", label: "SSN", to: "/TpSsnStatusCode" },
  ],
  Showcase: [
    {
      key: "TpShowcaseCategories",
      label: "Categories",
      to: "/TpShowcaseCategories",
    },
    {
      key: "TpShowEmailTemplate",
      label: "Email Template",
      to: "/TpShowEmailTemplate",
    },
    {
      key: "TpShowcaseLocations",
      label: "Locations",
      to: "/TpShowcaseLocations",
    },
    {
      key: "TpShowcaseParticipationStatus",
      label: "Status",
      to: "/TpShowcaseParticipationStatus",
    },
    // Public form link - render as external breadcrumb link (styled red) and open in new tab
    {
      key: null, // make the form link always visible (bypass permission checks)
      label: "Form",
      external: true,
      to: `${process.env.PUBLIC_URL || ""}/#/tech-prep-showcase`,
    },
    {
      key: "TpShowcaseSubmissions",
      label: "Submissions",
      to: "/TpShowcaseSubmissions",
    },
    {
      key: "TpShowcaseToggles",
      label: "Form Toggles",
      to: "/TpShowcaseToggles",
    },
  ],
};

// Main bar definitions (order as requested)
const mainCrumbs = [
  { label: "Courses", sub: true },
  { label: "Credit", sub: true },
  { key: "TpCtag", label: "CTAG/ITAG Catalog", to: "/TpCtag" },
  {
    key: "TpDeliveryDetails",
    label: "Delivery Details",
    to: "/TpDeliveryDetails",
  },
  { key: "TpDivisions", label: "Divisions", to: "/TpDivisions" },
  { label: "Elearn", sub: true },
  { label: "FPS", sub: true },
  { key: "TpHeiPrograms", label: "HEI Programs", to: "/TpHeiPrograms" },
  { label: "Partners", sub: true },
  {
    key: "TpPathwayPrograms",
    label: "Pathway Programs",
    to: "/TpPathwayPrograms",
  },
  { key: "ProgramDelivery", label: "Program Delivery", to: "/ProgramDelivery" },
  { label: "PTP", sub: true },
  { label: "Status Codes", sub: true },
  { label: "Showcase", sub: true },
  { label: "Students", sub: true },
];

const TechPrepPortalMgtBreadCrumbs = ({ pageName }) => {
  const location = useLocation();
  const theme = useTheme();
  const { userPermissions } = useAuth();
  const [openSub, setOpenSub] = useState(null); // e.g. 'Courses', 'Credit', etc.

  // Helper to check permission
  const canView = (areaKey) => {
    if (!areaKey) return true;
    const permissionName = routeToPermission[areaKey];
    return permissionName ? userPermissions[permissionName]?.view : false;
  };

  // Helper to render a link or a sub-menu trigger
  const renderLink = (item, idx, arr) => {
    if (item.sub) {
      const isOpen = openSub === item.label;
      return (
        <React.Fragment key={item.label}>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              textDecoration: "none",
              color: "#AC1A2F",
              background: isOpen ? "#f0e6e8" : "transparent",
              fontWeight: 700,
              fontSize: "0.95rem",
              cursor: "pointer",
              transition: "background 0.2s, color 0.2s",
              display: "flex",
              alignItems: "center",
              mr: idx < arr.length - 1 ? 0.5 : 0,
              mb: 0.5,
              minWidth: "80px",
              justifyContent: "center",
              border: isOpen ? "1px solid #AC1A2F" : "1px solid transparent",
            }}
            onClick={() => setOpenSub(isOpen ? null : item.label)}
          >
            {item.label}
          </Box>
          {idx < arr.length - 1 && (
            <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5, fontSize: 18 }} />
          )}
        </React.Fragment>
      );
    } else {
      if (!canView(item.key)) return null;
      const isActive = location.pathname === item.to;
      // External links (open in new window)
      if (item.external) {
        return (
          <React.Fragment key={item.to}>
            <Box
              component="a"
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                textDecoration: "none",
                color: "#AC1A2F",
                background: "transparent",
                fontWeight: 500,
                fontSize: "0.95rem",
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "#e0e0e0",
                  color: "#AC1A2F",
                },
                display: "flex",
                alignItems: "center",
                mr: idx < arr.length - 1 ? 0.5 : 0,
                mb: 0.5,
                minWidth: "80px",
                justifyContent: "center",
              }}
            >
              {item.label}
            </Box>
            {idx < arr.length - 1 && (
              <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5, fontSize: 18 }} />
            )}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={item.to}>
          <Box
            component={Link}
            to={item.to}
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              textDecoration: "none",
              color: isActive ? "#fff" : "#AC1A2F",
              background: isActive ? "#AC1A2F" : "transparent",
              fontWeight: isActive ? 700 : 500,
              fontSize: "0.95rem",
              transition: "background 0.2s, color 0.2s",
              "&:hover": {
                background: isActive ? "#AC1A2F" : "#e0e0e0",
                color: isActive ? "#fff" : "#AC1A2F",
              },
              display: "flex",
              alignItems: "center",
              mr: idx < arr.length - 1 ? 0.5 : 0,
              mb: 0.5,
              minWidth: "80px",
              justifyContent: "center",
            }}
          >
            {item.label}
          </Box>
          {idx < arr.length - 1 && (
            <ChevronRightIcon sx={{ color: "#bbb", mx: 0.5, fontSize: 18 }} />
          )}
        </React.Fragment>
      );
    }
  };

  // Render sub-links if a sub is open
  const renderSubLinks = (sub) => {
    const links = subCrumbs[sub] || [];
    return (
      <Box
        sx={{
          width: "100%",
          background: "#f5f5f5",
          py: 0.5,
          px: 2,
          borderRadius: 3,
          boxShadow: 1,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
          mt: 1,
        }}
      >
        {links.map((item, idx) => renderLink(item, idx, links))}
      </Box>
    );
  };

  // Find the active link label based on the current path
  const allLinks = [
    ...mainCrumbs.filter((l) => !l.sub),
    ...Object.values(subCrumbs).flat(),
  ];
  const activeLink = allLinks.find((item) => location.pathname === item.to);
  const displayName =
    pageName || (activeLink ? activeLink.label : "Tech Prep Portal");

  return (
    <>
      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 2 }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ color: "#222", textAlign: "center", mb: 1 }}
        >
          {displayName}
        </Typography>
      </Box>
      <Box
        sx={{
          width: "100%",
          background: "#f5f5f5",
          py: 0.5,
          px: 2,
          borderRadius: 3,
          boxShadow: 1,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          mb: 2,
          overflowX: "auto",
        }}
      >
        {mainCrumbs.map((item, idx) => renderLink(item, idx, mainCrumbs))}
      </Box>
      {openSub && renderSubLinks(openSub)}
    </>
  );
};

export default TechPrepPortalMgtBreadCrumbs;
